<script lang="ts">
import type { Colors, Sizes } from 'types';
import type { CheckboxRootProps } from 'reka-ui';

export interface CheckboxProps extends Pick<
  CheckboxRootProps,
  'disabled' | 'required' | 'name' | 'id' | 'defaultValue'
> {
  /**
   * The element or component this component should render as.
   * @defaultValue 'div'
   */
  as?: any;
  label?: string;
  description?: string;
  id?: string;
  icon?: string;
  color?: Colors;
  size?: Sizes;
  class?: string;
  /**
   * Sets the initial checked state.
   */
  checked?: boolean;
  /**
   * The value submitted when checked. Defaults to "true".
   */
  value?: string;
}

export interface CheckboxEmits {
  change: [value: boolean];
}
</script>

<script setup lang="ts">
import { computed, ref, watch, onMounted, getCurrentInstance, toRef } from 'vue';
import { Primitive, CheckboxRoot, CheckboxIndicator, useForwardProps } from 'reka-ui';
import { reactivePick } from '@vueuse/core';
import { useFormAssociated } from 'composables/useFormAssociated';

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<CheckboxProps>(), {
  color: 'neutral',
  size: 'md'
});
const emit = defineEmits<CheckboxEmits>();

const rootProps = useForwardProps(
  reactivePick(props, 'required', 'defaultValue', 'disabled', 'name')
);

// Expose props as refs so linter/template see them
const disabled = toRef(props, 'disabled');
const required = toRef(props, 'required');

const checked = ref(props.checked ?? false);
const host = ref<HTMLElement | null>(null);

// keep any user supplied value (or discover it from the host once)
const providedValue = ref<string | undefined>(props.value);

let isReflectingValueAttr = false;

const fallbackBooleanValue = computed(() => (checked.value ? 'true' : 'false'));

const submittedValue = computed(() => {
  if (providedValue.value !== undefined) {
    return checked.value ? providedValue.value : null;
  }
  return fallbackBooleanValue.value;
});

// Proxy input for validity + ElementInternals form value
const inputRef = ref<HTMLInputElement | null>(null);

const { validate } = useFormAssociated<string | null>({
  inputRef,
  modelValue: submittedValue,
  disabled,
  required,
  toFormValue: () => submittedValue.value
});

watch(
  () => props.checked,
  val => {
    if (val !== undefined) {
      checked.value = val;
    }
  }
);

watch(
  () => props.value,
  val => {
    if (isReflectingValueAttr) return;
    providedValue.value = val ?? undefined;
    updateHostAttributes();
    if (checked.value) validate();
  }
);

watch(checked, () => {
  updateHostAttributes();
  validate();
});

onMounted(() => {
  host.value = resolveHost();

  if (providedValue.value === undefined) {
    const attrValue = host.value?.getAttribute('value');
    if (attrValue != null) providedValue.value = attrValue;
  }

  if (props.checked === undefined && host.value?.hasAttribute('checked')) {
    checked.value = true;
  }

  updateHostAttributes();
  validate();
});

function resolveHost() {
  const instance = getCurrentInstance();
  const el = (instance?.proxy as any)?.$el as HTMLElement | null;
  if (!el) return null;
  return el.getRootNode() instanceof ShadowRoot
    ? ((el.getRootNode() as ShadowRoot).host as HTMLElement)
    : el;
}

function updateHostAttributes() {
  const element = host.value ?? resolveHost();
  if (!element) return;

  element.toggleAttribute('checked', checked.value);

  if (providedValue.value !== undefined) {
    if (element.getAttribute('value') !== providedValue.value) {
      isReflectingValueAttr = true;
      element.setAttribute('value', providedValue.value);
      isReflectingValueAttr = false;
    }
  } else if (element.hasAttribute('value')) {
    isReflectingValueAttr = true;
    element.removeAttribute('value');
    isReflectingValueAttr = false;
  }

  if (props.name) element.setAttribute('name', props.name);
}

function onUpdate(val: boolean | 'indeterminate') {
  const booleanValue = val === 'indeterminate' ? false : val;
  checked.value = booleanValue;
  emit('change', booleanValue);
}

/**
 * Toggle the checkbox when the label area is clicked.
 * Ignored when disabled.
 */
function onLabelClick(_: MouseEvent) {
  if (disabled.value) return;

  // Only handle clicks that occur inside the label container
  // (not on the checkbox root itself)
  onUpdate(!checked.value);
}

const classes = computed(() => [
  'e-checkbox',
  props.color && `e-checkbox-color-${props.color}`,
  props.size && `e-checkbox-size-${props.size}`,
  props.class
]);
</script>

<template>
  <primitive>
    <div
      class="e-checkbox-wrapper"
      :class="{ 'e-checkbox-disabled': disabled }"
    >
      <checkbox-root
        :id="id"
        v-bind="{ ...rootProps, ...$attrs }"
        v-model="checked"
        :class="classes"
        :name="name ?? id"
        @update:checked="onUpdate"
      >
        <checkbox-indicator
          force-mount
          class="e-checkbox-indicator"
        >
          <e-icon
            v-if="icon"
            :name="icon"
          />
        </checkbox-indicator>
      </checkbox-root>

      <div
        class="e-checkbox-label"
        @click="onLabelClick"
      >
        <e-label
          :id="id"
          :for="id"
        >
          <slot
            name="label"
            :label="label"
          >
            {{ label }}
          </slot>
        </e-label>
        <p class="e-checkbox-description">
          <slot
            name="description"
            :description="description"
          >
            {{ description }}
          </slot>
        </p>
      </div>
    </div>

    <input
      ref="inputRef"
      type="checkbox"
      :required="required"
      :disabled="disabled"
      :checked="checked"
      aria-hidden="true"
      tabindex="-1"
      class="e-checkbox-proxy"
      style="
        position: absolute;
        left: -9999px;
        width: 0;
        height: 0;
        opacity: 0;
        pointer-events: none;
      "
    />
  </primitive>
</template>
